import classnames from 'classnames'
import debounce from 'lodash.debounce'
import {
  clone,
  intersection,
  isEmpty,
  prop,
  without 
} from 'ramda'
import React,
{
  Component,
  Fragment 
} from 'react'

import { EmptyState } from '@driftt/crm-components'
import { Button } from '@driftt/dds'
import {
  Div,
  NavigationHeader,
  TextLoader 
} from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'
import {
  archiveContactGroup,
  getSingleSegment 
} from 'api/contacts'
import { createFastlanePlaybook } from 'api/playbooks'
import {
  archiveSingleSegment,
  deleteAllMembersInSegment,
  downloadSegmentCSV,
  exportSegmentCSV,
  findMembershipInNewSegment 
} from 'api/segments'
import Paginator from 'components/Paginator'
import SegmentDetailsSidebar from 'components/SegmentDetailsSidebar'
import SegmentTypes from 'components/Segments/constants/SegmentTypes'
import { Columns } from 'components/Segments/constants/SingleSegmentConstants'
import DeleteSegmentsConfirmationModal from 'components/Segments/DeleteSegmentsConfirmationModal'
import EnrollSegmentInPlaybook from 'components/Segments/EnrollSegmentInPlaybook'
import EnrollSegmentSuccessModal from 'components/Segments/EnrollSegmentSuccessModal'
import ExportEmailModal from 'components/Segments/ExportEmailModal'
import ManageSegmentList from 'components/Segments/SingleSegment/ManageSegmentList'
import { RowFormatter } from 'components/Segments/SingleSegment/RowFormatter'
import SegmentListControls from 'components/Segments/SingleSegment/SegmentListControls'
import Config from 'config'
import { delay } from 'utils'

import './style.css'

const NEW_SEGMENT_TYPES = ['CONTACT', 'STATIC', 'DYNAMIC']

class SingleSegmentList extends Component {
  constructor(props) {
    super(props)

    this.state = {
      segmentId: props.match.params.segmentId,
      pageSize: 25,
      currentPage: 0,
      totalPages: 0,
      index: 0,
      hasScrolled: false,
      isLoading: true,
      isCreatingFastlanePlaybook: false,
      isDeleteConfirmationModalOpen: false,
      viewFilterDetails: false,
      allSelected: false,
      firstLoad: true,
      currentFilter: '',
      targetModalOpen: false,
      segmentTimedOut: false,
      isExportEmailModalOpen: false
    }

    this.refetchMembersFromSearch = debounce(this.refetchMembers, 250)
  }

  componentDidMount() {
    this.getSegment()
  }

  getSegment = async () => {
    const {
      segmentId,
      pageSize,
      index 
    } = this.state
    const {
      user,
      onReceiveSegmentMembers,
      onReceiveSegmentDetails 
    } = this.props

    this.setState({ isLoading: true })

    try {
      const { data: segment } = await getSingleSegment(segmentId)

      onReceiveSegmentDetails(segment)

      const segmentType = SegmentTypes[segment.type]

      const { data } = await segmentType.fetchSegmentMembers({
        segment,
        pageSize,
        pageIndex: index,
        orgId: user.orgId 
      })

      if (data?.timedOut) {
        this.setState({ segmentTimedOut: true })
      }

      const segmentMembers = await segmentType.getSegmentMembersFromFetchData({ data })
      const totalNumberOfContacts = await segmentType.getTotalNumberOfContactsFromFetchData({
        data,
        segment 
      })

      onReceiveSegmentMembers(segmentMembers)
      
      this.setState({
        isLoading: false,
        totalContacts: totalNumberOfContacts,
        totalPages: Math.ceil(totalNumberOfContacts / pageSize),
        firstLoad: false
      })

    } catch(e) {
      console.warn(e)
    }
  }

  refetchMembers = () => {
    const { currentFilter } = this.state

    const isFiltered = !isEmpty(currentFilter)

    if (isFiltered) {
      this.filterMembers()
    } else {
      this.getSegment()
    }
  }

  changeSize = ({ value }) => {
    this.setState({ pageSize: value }, this.refetchMembers)
  }

  previousPage = () => {
    const {
      pageSize,
      index 
    } = this.state

    const newIndex = Math.max(index - pageSize, 0)

    this.setState(prevState => {
      const nextPage = prevState.currentPage - 1
      return {
        currentPage: nextPage,
        index: newIndex
      }
    }, this.refetchMembers)
  }

  nextPage = () => {
    const {
      pageSize,
      index,
      totalContacts 
    } = this.state

    const newIndex = Math.min(index + pageSize, totalContacts)

    this.setState(prevState => {
      const nextPage = prevState.currentPage + 1
      return {
        currentPage: nextPage,
        index: newIndex
      }
    }, this.refetchMembers)
  }

  setStickyHeader = (event) => {
    const { hasScrolled } = this.state
    const scrollingElement = event.target

    const offset = scrollingElement.scrollTop

    if (!hasScrolled && offset > 20) {
      this.setState({ hasScrolled: true })
    } else if (hasScrolled && offset === 0) {
      this.setState({ hasScrolled: false })
    }
  }

  selectAll = (isSelected) => {
    const {
      segmentList,
      onSelectMember,
      selectedMembers,
      onDeselectMember 
    } = this.props

    const selected = clone(selectedMembers)

    segmentList.forEach(member => selected.push(member.id))

    if (isSelected) {
      onSelectMember(selected)
    } else {
      onDeselectMember(selected)
    }

    this.setState({ allSelected: isSelected })
  }

  viewFilterDetails = () => {
    this.setState(prevState => ({ viewFilterDetails: !prevState.viewFilterDetails }))
  }

  clearSearch = () => {
    this.setState({ currentFilter: '' }, this.refetchMembers)
  }

  handleSearch = (value) => {
    if (value === '') {
      this.clearSearch()
    }

    this.setState({
      currentFilter: value,
      currentPage: 0,
      index: 0
    }, this.refetchMembersFromSearch)
  }

  filterMembers = async () => {
    const {
      segment,
      onReceiveSegmentMembers 
    } = this.props
    const {
      pageSize,
      index,
      currentFilter 
    } = this.state

    this.setState({ isLoading: true })

    try {
      const {
        data: {
          hydratedSegmentResults,
          total 
        } 
      } = await findMembershipInNewSegment(
        segment.id, index, pageSize, currentFilter
      )

      onReceiveSegmentMembers(hydratedSegmentResults)

      this.setState({
        isLoading: false,
        totalContacts: total,
        totalPages: Math.ceil(total / pageSize)
      })
    } catch (e) {
      console.warn(e)
    }
  }

  selectMember = (id, selected) => {
    const {
      onSelectMember,
      onDeselectMember 
    } = this.props

    if (selected) {
      onSelectMember(id)
    } else {
      onDeselectMember(id)
    }
  }

  deleteMembers = async () => {
    const {
      selectedMembers,
      showTransientNotification,
      onDeselectMember 
    } = this.props

    const selectedLength = selectedMembers.length

    this.setState({ isLoading: true })

    try {
      await archiveContactGroup(selectedMembers)

      await delay(this.getSegment, 1000)
      onDeselectMember([])
      showTransientNotification({
        type: 'success',
        message: `${selectedLength} contacts deleted` 
      })
    } catch(e) {
      console.warn(e)
    }
  }

  deleteAllMembers = async () => {
    const {
      user,
      showTransientNotification 
    } = this.props
    const { segmentId } = this.state

    try {
      await deleteAllMembersInSegment(user.orgId, segmentId)

      showTransientNotification({
        type: 'success',
        message: 'Successfully deleted all contacts in segment' 
      })
    } catch(e) {
      console.warn(e)

      showTransientNotification({
        type: 'error',
        message: 'Sorry, we couldnt delete all contacts. Try again later.' 
      })
    }
  }

  onCsvExport = () => {
    const {
      showTransientNotification,
      segment 
    } = this.props

    downloadSegmentCSV(segment.id)
      .then((response) => {
        const fileName = response.fileName
        const userMap = response.userList
        const headers = response.headers
        const csvRows = []
        csvRows.push(headers)
        userMap.forEach(user => {
          const row = [
            ...headers.map(header => user[header])
          ]  
          const escapeCsvValue = (value) => {
            if (value == null) {return ''}
            const str = String(value)
            if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
              return `"${str.replace(/"/g, '""')}"`
            }
            return str
          }
          const escapedRow = row.map(escapeCsvValue)
          csvRows.push(escapedRow.join(','))
        })
        const csvContent = csvRows.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const encodeUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = encodeUrl
        link.setAttribute('download', `${fileName}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(encodeUrl)
      })
    showTransientNotification({
      type: 'success',
      message: 'Your CSV is downloading' 
    })

  }

  onExport = async () => {
    const { user } = this.props
    if (user?.bot) {
      this.setState({ isExportEmailModalOpen: true })
      return
    }
    this.proceedWithExport()
  }

  proceedWithExport = async (recipientEmail = null) => {
    const {
      showTransientNotification,
      segment,
      user 
    } = this.props

    try {
      // If recipientEmail is provided, pass it to the API
      if (recipientEmail) {
        exportSegmentCSV(segment.id, recipientEmail)
      } else {
        exportSegmentCSV(segment.id, user.email)
      }

      showTransientNotification({
        type: 'success',
        message: 'Your segment list will be emailed to you soon!' 
      })
    } catch(e) {
      console.warn(e)
    }
  }

  handleExportEmailConfirm = (email) => {
    this.setState({ 
      isExportEmailModalOpen: false,
      recipientEmail: email 
    })
    this.proceedWithExport(email)
  }

  handleExportEmailClose = () => {
    this.setState({ isExportEmailModalOpen: false })
  }

  closeDeleteSegmentsModal = () => {
    this.setState({ isDeleteConfirmationModalOpen: false })
  }

  promptToDeleteSingleSegment = () => {
    this.setState({ isDeleteConfirmationModalOpen: true })
  }

  deleteSegment = async () => {
    const {
      history,
      segment,
      showTransientNotification 
    } = this.props

    try {
      await archiveSingleSegment(segment.id)

      history.push('/segments')

      showTransientNotification({
        type: 'success',
        message: 'Segment successfully deleted' 
      })
    } catch(e) {
      console.warn(e)
    }
  }

  targetPlaybook = () => {
    this.setState(prevState => ({ targetModalOpen: !prevState.targetModalOpen }))
  }

  openPlaybooks = () => {
    window.open('/playbooks', '_blank')
  }

  createFastlanePlaybook = async () => {
    const { segment } = this.props

    this.setState({ isCreatingFastlanePlaybook: true })

    const defaultPlaybookName = `Playbook for '${segment.name}'`
    const response = await createFastlanePlaybook({
      segment,
      name: defaultPlaybookName 
    })

    this.setState({ isCreatingFastlanePlaybook: false })
   
    const playbookUrl = response.url
    window.open(playbookUrl, '_blank')
  }

  goToContact = (contact) => {
    window.location.pathname = `/contacts/details/${contact}`
  }

  onSaveAudienceSuccess = (pbId) => {
    this.setState({ playbookEdited: pbId }, this.toggleSuccessModal)
  }

  toggleSuccessModal = () => {
    this.setState(prevState => ({ successfulSave: !prevState.successfulSave }))
  }

  goToPlaybook = () => {
    const { playbookEdited } = this.state

    window.location = `${Config.PLAYBOOK_EDITOR_URL}/${playbookEdited}`
  }

  renderEmptyState = () => {
    const {
      history,
      segment,
      segmentList 
    } = this.props
    const {
      currentFilter,
      isLoading 
    } = this.state

    const segmentIsNotEmpty = segmentList.length > 0

    if (segmentIsNotEmpty || isLoading) {
      return null
    }

    const isFiltered = !isEmpty(currentFilter)

    if (segment.type === 'PARDOT') {
      return (
        <EmptyState
          topText="There are no contacts in this Pardot segment (yet!)"
          bottomText={
            <Div>Check back later to see if your Pardot List has finished importing.</Div>
          }
        />
      )
    }

    if (segment.type === 'MARKETO') {
      return (
        <EmptyState
          history={history}
          topText="There are no contacts in this Marketo segment (yet!)"
          bottomText={
            <Fragment>
              <Div>Check back later to see if your Marketo List has finished importing.</Div>
              <Div>You can also view your Marketo activity log to make sure there were no errors.</Div>
            </Fragment>
          }
          ctaText="View Marketo Activity"
          ctaUrl="/imports/logs"
        />
      )
    }

    if (isFiltered) {
      return (
        <EmptyState
          history={history}
          topText="We couldn’t find any contacts matching your filters"
          bottomText="Try broadening your search terms to see results here."
          ctaButton={<Button variant="secondary" onClick={this.clearSearch}>Clear search</Button>}
        />
      )
    }

    return (
      <EmptyState
        history={history}
        topText="Oops! There are no contacts in this segment."
        bottomText="You can either delete this segment or create a new one."
        ctaText="Create a new Segment"
        ctaUrl="/?createSegment=true"
      />
    )
  }

  render() {
    const {
      currentFilter,
      currentPage,
      firstLoad,
      hasScrolled,
      isCreatingFastlanePlaybook,
      isDeleteConfirmationModalOpen,
      isExportEmailModalOpen,
      isLoading,
      pageSize,
      segmentTimedOut,
      successfulSave,
      targetModalOpen,
      totalContacts,
      totalPages,
      viewFilterDetails,
    } = this.state

    const {
      segmentList,
      segment,
      selectedMembers 
    } = this.props

    const { isSystemOwned } = segment

    const segmentIsEmpty = segmentList.length === 0

    const currentPageIds = segmentList.map(prop('id'))
    const isEntirePageSelected = isEmpty(without(selectedMembers, currentPageIds))
    const selectedContactsOnCurrentPage = intersection(selectedMembers, currentPageIds)
    const isPartiallySelected = selectedContactsOnCurrentPage.length > 0

    return (
      <Div className="single-segment--container">
        {!isEmpty(segment) &&
          <NavigationHeader title={segment.name}>
            {!firstLoad &&
              <ManageSegmentList
                isSystemOwned={isSystemOwned}
                isCreatingFastlanePlaybook={isCreatingFastlanePlaybook}
                onShowDetails={this.viewFilterDetails}
                onCloneSegment={this.onCloneSegment}
                onExport={this.onExport}
                onCsvExport={this.onCsvExport}
                onDeleteSelected={this.deleteMembers}
                onDeleteAllContacts={this.deleteAllMembers}
                onDeleteSegment={this.promptToDeleteSingleSegment}
                onAddSegmentToPlaybook={this.targetPlaybook}
                onCreatePlaybook={this.openPlaybooks}
              />
            }
          </NavigationHeader>
        }
        {!firstLoad && 
          <SegmentListControls 
            isEntirePageSelected={isEntirePageSelected}
            isPartiallySelected={isPartiallySelected}
            deleteSelected={this.deleteMembers}
            searchQuery={currentFilter}
            isLegacy={NEW_SEGMENT_TYPES.some(type => type === segment.type)}
            onClear={this.selectAll}
            onSelectAll={this.selectAll}
            onSearch={this.handleSearch}
            onViewFilter={this.viewFilterDetails}
            totalMembers={totalContacts}
            segmentTimedOut={segmentTimedOut}
          />
        }
        <div className={classnames('single-segment-table--container', { 'single-segment-table--container--stick': hasScrolled })} 
          onScroll={this.setStickyHeader}
        >
          {!firstLoad && 
            <Table
              isWorking={isLoading}
              columns={Columns()}
              data={!!segmentList && segmentList.map(member => RowFormatter(
                member, this.deleteMember, this.viewMemberDetails, this.selectMember, selectedMembers, this.goToContact
              ))}
              className="single-segment-table"
            />
          }
          {firstLoad && 
            <TextLoader text="Grabbing members for this segment"  className="single-segment--loader"/>
          }
          {this.renderEmptyState()}
        </div>
        {viewFilterDetails &&
          <SegmentDetailsSidebar isOpen={viewFilterDetails} onClose={this.viewFilterDetails}
            isLegacy={NEW_SEGMENT_TYPES.some(type => type !== segment.type)} />
        }
        <EnrollSegmentInPlaybook
          open={targetModalOpen}
          segment={segment}
          onClose={this.targetPlaybook}
          onSaveSuccess={this.onSaveAudienceSuccess}
        />
        <EnrollSegmentSuccessModal 
          open={successfulSave}
          onClose={this.toggleSuccessModal}
          goToPlaybook={this.goToPlaybook}
        />
        <DeleteSegmentsConfirmationModal 
          open={isDeleteConfirmationModalOpen}
          segmentsToDelete={isEmpty(segment) ? [] : [segment]}
          onConfirm={this.deleteSegment}
          onClose={this.closeDeleteSegmentsModal}
        />
        <ExportEmailModal
          open={isExportEmailModalOpen}
          onConfirm={this.handleExportEmailConfirm}
          onClose={this.handleExportEmailClose}
        />
        {!segmentIsEmpty &&
          <Paginator
            goForward={this.nextPage}
            goBackward={this.previousPage}
            totalPages={totalPages}
            size={pageSize}
            index={currentPage}
            changeSize={this.changeSize}
          />
        }
      </Div>
    )
  }
}

export default SingleSegmentList
